import { notification } from 'antd'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  createMcpMock,
  deleteMcpMock,
  listMcpMocks,
  updateMcpMock,
} from './mcpMocksAdminApi'
import {
  McpMockCreateInput,
  McpMockFilters,
  McpMockUpdateInput,
} from './mcpMocksAdminModel'

const ADMIN_LIST_KEY = ['mcpMocksAdmin', 'list'] as const

export const useMcpMocksListQuery = (filters: McpMockFilters) => {
  return useQuery({
    queryKey: [...ADMIN_LIST_KEY, filters],
    queryFn: () => listMcpMocks(filters),
    retry: false,
    // Hold the prior page on the screen while a new search/page loads.
    keepPreviousData: true,
    onError: () => {
      notification.error({ message: 'Failed to load MCP mocks' })
    },
  })
}

export const useCreateMcpMockMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((input: McpMockCreateInput) => createMcpMock(input), {
    onSuccess: () => {
      queryClient.invalidateQueries(ADMIN_LIST_KEY)
      notification.success({ message: 'MCP mock created' })
    },
    onError: (err) => {
      const description = err instanceof Error ? err.message : undefined
      notification.error({ message: 'Failed to create MCP mock', description })
    },
  })
}

export const useUpdateMcpMockMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((input: McpMockUpdateInput) => updateMcpMock(input), {
    onSuccess: () => {
      queryClient.invalidateQueries(ADMIN_LIST_KEY)
      notification.success({ message: 'MCP mock updated' })
    },
    onError: (err) => {
      const description = err instanceof Error ? err.message : undefined
      notification.error({ message: 'Failed to update MCP mock', description })
    },
  })
}

export const useDeleteMcpMockMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((scenarioId: string) => deleteMcpMock(scenarioId), {
    onSuccess: () => {
      queryClient.invalidateQueries(ADMIN_LIST_KEY)
      notification.success({ message: 'MCP mock deleted' })
    },
    onError: (err) => {
      const description = err instanceof Error ? err.message : undefined
      notification.error({ message: 'Failed to delete MCP mock', description })
    },
  })
}
